import { useQueryClient } from "@tanstack/react-query";
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { hubConnection } from "signalr-no-jquery";
import AuthClient from "../api/AuthClient";
import SignalRService from "../ChatService/SignalRService";
import { RealtimeUpdateEvent, SendMessageCommandParameters } from "../types";
import { getCurrentCity } from "../utils/currentCity";
import { CitiesInfoContext } from "../AllCitiesContext";
import { applyRealtimeUpdateToQueryCache } from "../query/realtimeInvalidation";

interface SignalRContextData {
    isConnected: boolean;
    joinChatRoom: (roomId: string) => Promise<void>;
    sendMessage: (message: SendMessageCommandParameters) => Promise<void>;
    subscribe: (eventName: string, handler: (...args: any[]) => void) => () => void;
}

const noopAsync = async () => {};

export const SignalRContext = createContext<SignalRContextData>({
    isConnected: false,
    joinChatRoom: noopAsync,
    sendMessage: noopAsync,
    subscribe: () => () => {},
});

const getUpdateChannelName = (cityId?: string) =>
    cityId ? `updates:cities:${cityId}` : "";

export const SignalRConnectionStateProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [isConnected, setSignalRConnectionState] = useState<boolean>(false);
    const [authVersion, setAuthVersion] = useState(0);
    const queryClient = useQueryClient();
    const { selectedCity } = useContext(CitiesInfoContext);
    const signalRServiceRef = useRef<SignalRService | null>(null);
    const joinedUpdateChannelRef = useRef<string>("");
    const joiningUpdateChannelRef = useRef<string>("");

    if (!signalRServiceRef.current) {
        const connection = hubConnection(process.env.REACT_APP_CHAT_URL);
        const hubProxy = connection.createHubProxy("ChatHub");
        signalRServiceRef.current = new SignalRService(
            connection,
            hubProxy,
            setSignalRConnectionState
        );
    }

    const signalRService = signalRServiceRef.current;

    useEffect(() => {
        const refreshAuth = () => setAuthVersion((value) => value + 1);

        window.addEventListener("login", refreshAuth);
        window.addEventListener("logout", refreshAuth);

        return () => {
            window.removeEventListener("login", refreshAuth);
            window.removeEventListener("logout", refreshAuth);
        };
    }, []);

    const invalidateRealtimeQueries = useCallback((event: RealtimeUpdateEvent) => {
        applyRealtimeUpdateToQueryCache(queryClient, event);
    }, [queryClient]);

    useEffect(() => {
        const handleEntityChanged = (event: RealtimeUpdateEvent) => {
            console.log("Received realtime update via SignalR.", event);
            invalidateRealtimeQueries(event);
        };

        signalRService.on("entityChanged", handleEntityChanged);

        return () => {
            signalRService.off("entityChanged", handleEntityChanged);
        };
    }, [invalidateRealtimeQueries, signalRService]);

    useEffect(() => {
        const authClient = new AuthClient();

        if (!authClient.isLoggedIn()) {
            if (joinedUpdateChannelRef.current) {
                void signalRService.leaveUpdateChannel(joinedUpdateChannelRef.current);
                joinedUpdateChannelRef.current = "";
                joiningUpdateChannelRef.current = "";
            }
            signalRService.stopConnection();
            return;
        }

        const authResponse = authClient.getAuthenticationResponse();
        const cityId = getCurrentCity(authResponse)?.cityId;
        const nextChannelName = getUpdateChannelName(cityId);

        if (!nextChannelName) {
            return;
        }

        void (async () => {
            await signalRService.startConnection();

            if (
                joinedUpdateChannelRef.current &&
                joinedUpdateChannelRef.current !== nextChannelName
            ) {
                await signalRService.leaveUpdateChannel(joinedUpdateChannelRef.current);
            }

            if (joinedUpdateChannelRef.current !== nextChannelName) {
                if (joiningUpdateChannelRef.current === nextChannelName) {
                    return;
                }

                joiningUpdateChannelRef.current = nextChannelName;
                await signalRService.joinUpdateChannel(nextChannelName);
                joinedUpdateChannelRef.current = nextChannelName;
                joiningUpdateChannelRef.current = "";
            }
        })().catch((error) => {
            joiningUpdateChannelRef.current = "";
            console.error("Unable to start SignalR update connection", error);
        });
    }, [authVersion, selectedCity, signalRService]);

    const joinChatRoom = useCallback(
        async (roomId: string) => {
            await signalRService.startConnection();
            await signalRService.joinChatRoom(roomId);
        },
        [signalRService]
    );

    const sendMessage = useCallback(
        async (message: SendMessageCommandParameters) => {
            await signalRService.sendMessage(message);
        },
        [signalRService]
    );

    const subscribe = useCallback(
        (eventName: string, handler: (...args: any[]) => void) => {
            signalRService.on(eventName, handler);

            return () => {
                signalRService.off(eventName, handler);
            };
        },
        [signalRService]
    );

    const value = useMemo(
        () => ({
            isConnected,
            joinChatRoom,
            sendMessage,
            subscribe,
        }),
        [isConnected, joinChatRoom, sendMessage, subscribe]
    );

    return (
        <SignalRContext.Provider value={value}>
            {children}
        </SignalRContext.Provider>
    );
};
